import React, { useState, useCallback } from "react";
import { IResourceListProps } from "../../common/ra-types";
import {
  useListContext,
  Filter,
  TextInput,
  TopToolbar,
  sanitizeListRestProps,
  CreateButton,
  Datagrid,
  TextField,
  useRefresh,
  Button,
  Identifier,
  Confirm,
  useNotify,
  useUnselectAll,
  useTranslate,
  BooleanField,
  Record,
  Responsive,
} from "react-admin";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import RouterOutlinedIcon from "@material-ui/icons/RouterOutlined";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { RouteComponentProps } from "react-router-dom";
import { History } from "history";
import {
  Divider,
  Dialog,
  DialogTitle,
  List as MuiList,
  ListItem,
  ListItemText,
  ListItemIcon,
  Theme,
  useTheme,
  Typography,
} from "@material-ui/core";
import "./EldsList.css";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { MasterEldDeviceApiFactory } from "../../../common/data/service-proxies";
import resourceNames from "../../../common/data/resourceNames";
import { useAppData } from "../../../common/hooks/useAppData";
import FolderSharedOutlinedIcon from "@material-ui/icons/FolderSharedOutlined";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { ShowEldMessagesMenuItem } from "./ShowEldMessagesMenuItem";
import SelectSimpleList from "../../hos/unidentified-driving/SimpleSelectList";
import { ShowEldUsageMenuItem } from "./ShowEldUsageMenuItem";

interface Props extends IResourceListProps, Partial<RouteComponentProps> {}
interface BulkActionProps {
  history?: History<any>;
  onClose: () => void;
  selectedIds?: Array<Identifier>;
}

const ListFilter = (props: any) => {
  const { loading } = useListContext();
  useSearchFields(props, ["serialNumber"]);
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="fleet-managers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="fleet-managers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

const ListActions = (props: any) => {
  const { className, exporter, filters, maxResults, ...rest } = props;
  const { basePath } = useListContext();
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      <CreateButton basePath={basePath} />
    </TopToolbar>
  );
};

const BulkActions = ({ onClose, selectedIds }: BulkActionProps) => {
  const notify = useNotify();
  const axios = useAxiosInstance();
  const tenants = useAppData((x) => x.tenants) ?? [];
  const translate = useTranslate();
  const [unassignConfirmOpen, setUnassignConfirmOpen] = useState<boolean>(
    false
  );
  const [assignDialogOpen, setAssignDialogOpen] = useState<boolean>(false);
  const handleAssign = useCallback(
    (tenantId: string) => {
      if (!axios) {
        return;
      }

      if (!selectedIds) {
        return;
      }

      MasterEldDeviceApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppMasterelddeviceAssigntotenantPost({
          selectedIds: selectedIds.map((x) => Number(x)),
          tenantId,
        })
        .then(() => {
          setAssignDialogOpen(false);
          onClose();
          notify("resources.master-elds.labels.assignSuccessful", "success");
        })
        .catch((e) => {
          notify("resources.master-elds.labels.assignFailed", "error");
          setUnassignConfirmOpen(false);
        });
    },
    [axios, selectedIds, onClose, notify]
  );
  const handleUnassign = useCallback(() => {
    if (!axios) {
      return;
    }

    if (!selectedIds) {
      return;
    }

    MasterEldDeviceApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMasterelddeviceUnassignfromtenantsPost({
        selectedIds: selectedIds.map((x) => Number(x)),
      })
      .then(() => {
        setUnassignConfirmOpen(false);
        onClose();
        notify("resources.master-elds.labels.unassignSuccessful", "success");
      })
      .catch((e) => {
        notify("resources.master-elds.labels.unassignFailed", "error");
        setUnassignConfirmOpen(false);
      });
  }, [selectedIds, axios, notify, onClose]);
  return (
    <div className="eld-bulk-actions">
      <Button
        label="resources.master-elds.labels.unassign"
        onClick={() => setUnassignConfirmOpen(true)}
      />
      <Divider />
      <Button
        label="resources.master-elds.labels.assign"
        onClick={() => setAssignDialogOpen(true)}
      />
      <Confirm
        isOpen={unassignConfirmOpen}
        title="resources.master-elds.labels.confirmUnassignTitle"
        content="resources.master-elds.labels.confirmUnassignMessage"
        onConfirm={handleUnassign}
        onClose={() => setUnassignConfirmOpen(false)}
      />
      <Dialog
        onClose={() => setAssignDialogOpen(false)}
        open={assignDialogOpen}
      >
        <DialogTitle>
          {translate("resources.master-elds.labels.confirmAssignTitle")}
        </DialogTitle>
        <MuiList>
          {tenants.map(
            (x) =>
              x.id && (
                <ListItem button onClick={() => handleAssign(x.id!)} key={x.id}>
                  <ListItemIcon>
                    <FolderSharedOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary={x.name} />
                </ListItem>
              )
          )}
        </MuiList>
      </Dialog>
    </div>
  );
};

export const EldsList = (props: Props) => {
  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const theme: Theme = useTheme();

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            ELD S/N:
          </Typography>
          <TextField
            record={record}
            source="serialNumber"
            label="resources.master-elds.labels.serialNumber"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Vendor:
          </Typography>
          <TextField
            record={record}
            source="vendor"
            label="resources.master-elds.labels.vendor"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Assigned to tenant:
          </Typography>
          <TextField
            record={record}
            source="assignedToTenant"
            label="resources.master-elds.labels.assignedToTenant"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "6px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: "600", color: theme.palette.text.secondary }}
          >
            Debugging enabled:
          </Typography>
          <BooleanField
            record={record}
            source="debuggingEnabled"
            className="centered-content"
            headerClassName="centered-content"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={RouterOutlinedIcon} />}
        className="content-list"
        title="resources.master-elds.name"
        bulkActionButtons={
          <BulkActions
            history={props.history}
            onClose={() => {
              unselectAll(resourceNames.MASTER_ELDS);
              refresh();
            }}
          />
        }
        actions={<ListActions />}
        filters={<ListFilter />}
        {...props}
      >
        <Responsive
          small={
            <SelectSimpleList
              className="simple-list-wrapper"
              linkType="edit"
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList record={record} />
              )}
              // secondaryText={(record) => (
              //   <RenderSecondaryTextForSimpleList record={record} />
              // )}
              rightIcon={(record) => {
                return (
                  <ActionsField
                    {...props}
                    record={record}
                    editable={(_) => true}
                  >
                    <ShowEldMessagesMenuItem />
                  </ActionsField>
                );
              }}
            />
          }
          medium={
            <Datagrid>
              <TextField
                source="serialNumber"
                label="resources.master-elds.labels.serialNumber"
              />
              <TextField
                source="vendor"
                label="resources.master-elds.labels.vendor"
              />
              <TextField
                source="assignedToTenant"
                label="resources.master-elds.labels.assignedToTenant"
              />
              <BooleanField
                source="debuggingEnabled"
                className="centered-content"
                headerClassName="centered-content"
              />
              <BooleanField
                source="dataPlanEnabled"
                label="Data plan enabled"
                className="centered-content"
                headerClassName="centered-content"
              />
              <BooleanField
                source="hasNote"
                label="Has notes"
                className="centered-content"
                headerClassName="centered-content"
              />
              <ActionsField editable={(_) => true}>
                <ShowEldMessagesMenuItem />
                <ShowEldUsageMenuItem />
              </ActionsField>
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
